import React, { useCallback, useMemo } from 'react';

import {
  Box,
  Checkbox,
  Link,
  ListItemIcon,
  makeStyles,
  MenuItem,
  Typography,
  useTheme,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import GetAppIcon from '@material-ui/icons/GetApp';
import ReplyIcon from '@material-ui/icons/Reply';
import clsx from 'clsx';
import { useConfirm } from 'material-ui-confirm';
import randomColor from 'randomcolor';

import { ReactComponent as EncaminharMensagensIcon } from 'assets/images/forward-icon.svg';
import AudioMessage from 'components/AudioMessage';
import CommentMessage from 'components/CommentMessage';
import ContatoMessage from 'components/ContatoMessage';
import DocumentMessage from 'components/DocumentMessage';
import EventMessage from 'components/EventMessage';
import ImageMessage from 'components/ImageMessage';
import LocationMessage from 'components/LocationMessage';
import TextMessage from 'components/TextMessage';
import VideoMessage from 'components/VideoMessage';
import useCurrentUser from 'hooks/useCurrentUser';
import { useAppDispatch, useAppSelector } from 'store';
import { StatusSessaoEnum } from 'store/reducers/atendimentos';
import { TipoCanalEnum } from 'store/reducers/canais';
import {
  MensagemInterface,
  resetMensagensParaEncaminhar,
  setMensagensParaEncaminhar,
  TiposMensagemEnum,
} from 'store/reducers/mensagens';
import getUsuarioNomeExibicao from 'utils/getUsuarioNomeExibicao';

import CtaUrlButtonMessage from './CtaUrlButtonMessage';
import FallbackMessage from './FallbackMessage';
import IASummaryMessage from './IASummaryMessage';
import InteractiveListMessage from './InteractiveListMessage';
import MultiContatoMessage from './MultiContatoMessage';
// import PendingMediaMessage from './PendingMediaMessage';
import ReplyButtonsMessage from './ReplyButtonsMessage';
import TemplateMessage from './TemplateMessage';

const useStyles = makeStyles(({ palette, spacing }) => ({
  selectableMessage: {
    cursor: 'pointer',
    borderRadius: spacing(1),
    marginBlock: 1,
    '&:hover': {
      backgroundColor: palette.action.selected,
    },
  },
  selectedMessage: {
    borderRadius: spacing(1),
    marginBlock: 1,
    backgroundColor: palette.action.selected,
  },
}));

const MensagemTipo = {
  [TiposMensagemEnum.AUDIO]: AudioMessage,
  [TiposMensagemEnum.COMENTARIO]: CommentMessage,
  [TiposMensagemEnum.CONTATO]: ContatoMessage,
  [TiposMensagemEnum.DOCUMENTO]: DocumentMessage,
  [TiposMensagemEnum.EVENTO]: EventMessage,
  [TiposMensagemEnum.TEXTO]: TextMessage,
  [TiposMensagemEnum.IMAGEM]: ImageMessage,
  [TiposMensagemEnum.LOCALIZACAO]: LocationMessage,
  [TiposMensagemEnum.VIDEO]: VideoMessage,
  [TiposMensagemEnum.TEMPLATE_WHATSAPP]: TemplateMessage,
  [TiposMensagemEnum.WPP_CTA]: CtaUrlButtonMessage,
  [TiposMensagemEnum.WPP_REPLY_BTN]: ReplyButtonsMessage,
  [TiposMensagemEnum.WPP_REPLY_BTN_RESP]: TextMessage,
  [TiposMensagemEnum.WPP_LIST]: InteractiveListMessage,
  [TiposMensagemEnum.WPP_LIST_RESP]: TextMessage,
  [TiposMensagemEnum.ARRAY_CONTATOS]: MultiContatoMessage,
  [TiposMensagemEnum.RESUMO_IA]: IASummaryMessage,
  // [TiposMensagemEnum.MIDIA]: PendingMediaMessage,
};

const typesToAllowForwarding = [
  TiposMensagemEnum.TEXTO,
  TiposMensagemEnum.IMAGEM,
  TiposMensagemEnum.DOCUMENTO,
  TiposMensagemEnum.AUDIO,
  TiposMensagemEnum.VIDEO,
  TiposMensagemEnum.LOCALIZACAO,
  TiposMensagemEnum.CONTATO,
  TiposMensagemEnum.ARRAY_CONTATOS,
];

interface MessageItemProps {
  mensagem: MensagemInterface;
  onResponderMensagem?: (mensagem: MensagemInterface) => void;
  onApagarMensagem?: (mensagemId: string) => void;
  onEditarMensagem?: (mensagem: MensagemInterface) => void;
  onReenviarMensagem?: (mensagem: MensagemInterface) => void;
  onVerHistoricoMensagemEditada?: (mensagemId: string) => void;
  isGroup?: boolean;
  verMensagemCitadaNoModal?: boolean;
  historicoAtendimento?: boolean;
  actions?: React.ReactNode[] | false;
}

function MessageItem({
  mensagem,
  onApagarMensagem,
  onEditarMensagem,
  onReenviarMensagem,
  onResponderMensagem,
  onVerHistoricoMensagemEditada,
  isGroup = false,
  verMensagemCitadaNoModal = false,
  historicoAtendimento = false,
  actions,
}: MessageItemProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const atendimentoSelecionado = useAppSelector(
    state => state.atendimentos.atendimentoSelecionado,
  );
  const isEncaminhandoMensagens = useAppSelector(
    state => state.mensagens.isEncaminhandoMensagens,
  );
  const mensagensParaEncaminhar = useAppSelector(
    state => state.mensagens.mensagensParaEncaminhar,
  );
  const mensagemIdPesquisada = useAppSelector(
    state => state.mensagens.mensagemIdPesquisada,
  );

  const currentUser = useCurrentUser();
  const theme = useTheme();
  const confirm = useConfirm();

  const handleApagarMensagem = useCallback(
    (mensagemId: string) => {
      confirm({
        title: <Typography variant="body2">Apagar mensagem</Typography>,
        description: (
          <Typography variant="caption">
            Deseja mesmo apagar esta mensagem? Após esta ação o contato não
            poderá mais vê-la.
          </Typography>
        ),
        confirmationText: 'Apagar',
        cancellationText: 'Cancelar',
        confirmationButtonProps: { color: 'secondary', variant: 'contained' },
        dialogProps: { maxWidth: 'xs' },
      })
        .then(() => {
          if (onApagarMensagem) onApagarMensagem(mensagemId);
        })
        .catch(() => {
          //
        });
    },
    [confirm, onApagarMensagem],
  );

  const handleResponderMensagem = useCallback(
    (msg: MensagemInterface) => {
      if (onResponderMensagem) onResponderMensagem(msg);
    },
    [onResponderMensagem],
  );

  const getAutor = useCallback((msg: MensagemInterface) => {
    const { autor, usuario } = msg;
    const msgUsuario = getUsuarioNomeExibicao(usuario);

    if (autor === 'BOT') return 'Bot';
    if (autor === 'ADMINISTRADOR') return `ADM: ${msgUsuario}`;
    if (autor === 'AUTO') return '';
    if (autor === 'ATENDENTE_EXTERNO') return '';

    return msgUsuario;
  }, []);

  const handleCancelarEncaminharMensagens = useCallback(() => {
    dispatch(resetMensagensParaEncaminhar());
  }, [dispatch]);

  const handleToggleEncaminhar = useCallback(() => {
    dispatch(setMensagensParaEncaminhar({ id: mensagem.id }));
  }, [dispatch, mensagem.id]);

  const messageData = useMemo(() => {
    const {
      id,
      autor,
      mensagemApagadaEm,
      isEncaminhada,
      usuario,
      mensagemCitada,
      midiaUrl,
      tipo,
      contato,
    } = mensagem;

    const fromMe = autor !== 'CONTATO';
    const fromCurrentUser = !!(usuario?.id === currentUser?.id || !usuario);
    const customAutor = contato?.nome ?? getAutor(mensagem);
    const autorColor = randomColor({
      seed: customAutor,
      luminosity: theme.palette.type === 'dark' ? 'light' : 'dark',
    });

    const tipoCanal = atendimentoSelecionado?.canal?.tipoCanal;
    const closedSession =
      atendimentoSelecionado?.sessaoStatus === StatusSessaoEnum.CLOSED;

    const isOfficialWhatsAppChannel =
      tipoCanal === TipoCanalEnum.WHATSAPP_OFICIAL;
    const isOfficialWhatsAppChannelWithClosedSession =
      isOfficialWhatsAppChannel && closedSession;

    const isInstagramOrMessengerChannel =
      tipoCanal === TipoCanalEnum.INSTAGRAM ||
      tipoCanal === TipoCanalEnum.MESSENGER;

    const isIntagramChannelWithClosedSession =
      isInstagramOrMessengerChannel && closedSession;

    const canForward =
      typesToAllowForwarding.includes(tipo) &&
      !historicoAtendimento &&
      !mensagemApagadaEm;
    const isSelected =
      mensagensParaEncaminhar.includes(id) && !historicoAtendimento;
    const isSelectable =
      isEncaminhandoMensagens && canForward && mensagemApagadaEm === null;

    return {
      id,
      autor,
      mensagemApagadaEm,
      isEncaminhada,
      usuario,
      mensagemCitada,
      midiaUrl,
      tipo,
      fromMe,
      fromCurrentUser,
      customAutor,
      autorColor,
      isOfficialWhatsAppChannel,
      isInstagramOrMessengerChannel,
      isOfficialWhatsAppChannelWithClosedSession,
      isIntagramChannelWithClosedSession,
      canForward,
      isSelected,
      isSelectable,
    };
  }, [
    mensagem,
    currentUser?.id,
    getAutor,
    theme.palette.type,
    atendimentoSelecionado?.canal?.tipoCanal,
    atendimentoSelecionado?.sessaoStatus,
    historicoAtendimento,
    mensagensParaEncaminhar,
    isEncaminhandoMensagens,
  ]);

  const defaultMessageActions = useMemo(
    () => [
      (messageData.autor === 'ATENDENTE' ||
        messageData.autor === 'ATENDENTE_EXTERNO') &&
        messageData.fromCurrentUser &&
        !messageData.isOfficialWhatsAppChannel &&
        !messageData.isInstagramOrMessengerChannel &&
        !messageData.isEncaminhada &&
        messageData.tipo === TiposMensagemEnum.TEXTO &&
        onEditarMensagem && (
          <MenuItem key="editar" onClick={() => onEditarMensagem(mensagem)}>
            <ListItemIcon style={{ minWidth: 28 }}>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">Editar</Typography>
          </MenuItem>
        ),
      onResponderMensagem && !messageData.isInstagramOrMessengerChannel && (
        <MenuItem
          key="responder"
          onClick={() => handleResponderMensagem(mensagem)}
        >
          <ListItemIcon style={{ minWidth: 28 }}>
            <ReplyIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Responder</Typography>
        </MenuItem>
      ),
      messageData.canForward && (
        <MenuItem key="encaminhar" onClick={handleToggleEncaminhar}>
          <ListItemIcon style={{ minWidth: 28 }}>
            <EncaminharMensagensIcon
              style={{ width: 18, height: 18 }}
              color="action"
            />
          </ListItemIcon>
          <Typography variant="body2">Encaminhar</Typography>
        </MenuItem>
      ),
      messageData.midiaUrl && (
        <MenuItem
          key="download"
          component={Link}
          color="inherit"
          underline="none"
          href={`${messageData.midiaUrl}?download=true`}
          rel="no-referrer"
          target="_blank"
        >
          <ListItemIcon style={{ minWidth: 28 }}>
            <GetAppIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Baixar</Typography>
        </MenuItem>
      ),
      (messageData.autor === 'ATENDENTE' ||
        messageData.autor === 'ATENDENTE_EXTERNO') &&
        messageData.fromCurrentUser &&
        !messageData.isOfficialWhatsAppChannel &&
        !messageData.isInstagramOrMessengerChannel &&
        onApagarMensagem && (
          <MenuItem
            key="apagar"
            onClick={() => handleApagarMensagem(messageData.id)}
          >
            <ListItemIcon style={{ minWidth: 28 }}>
              <DeleteIcon color="error" fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2" color="secondary">
              Apagar
            </Typography>
          </MenuItem>
        ),
    ],
    [
      messageData,
      onApagarMensagem,
      onEditarMensagem,
      onResponderMensagem,
      mensagem,
      handleApagarMensagem,
      handleResponderMensagem,
      handleToggleEncaminhar,
    ],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape' && !event.shiftKey) {
        event.preventDefault();
        handleCancelarEncaminharMensagens();
      }
    },
    [handleCancelarEncaminharMensagens],
  );

  const handleClick = useCallback(() => {
    if (isEncaminhandoMensagens && messageData.canForward) {
      handleToggleEncaminhar();
    }
  }, [isEncaminhandoMensagens, messageData.canForward, handleToggleEncaminhar]);

  const MessageComponent = MensagemTipo[mensagem.tipo];

  return (
    <div
      style={{ display: 'flex', alignItems: 'center' }}
      className={clsx({
        [classes.selectableMessage]: messageData.isSelectable,
        [classes.selectedMessage]: messageData.isSelected,
      })}
    >
      {messageData.isSelectable && (
        <Checkbox
          checked={messageData.isSelected}
          color="primary"
          onChange={handleToggleEncaminhar}
        />
      )}
      <Box flexGrow={1} onClick={handleClick} onKeyDown={handleKeyDown}>
        {MessageComponent ? (
          <MessageComponent
            fromMe={messageData.fromMe}
            autor={
              !messageData.fromMe && !isGroup ? '' : messageData.customAutor
            }
            autorColor={messageData.autorColor}
            apagada={Boolean(messageData.mensagemApagadaEm)}
            encaminhada={messageData.isEncaminhada}
            mensagem={mensagem}
            isGroup={isGroup}
            mensagemCitada={messageData.mensagemCitada}
            isMensagemPesquisada={mensagemIdPesquisada === messageData.id}
            verMensagemCitadaNoModal={
              verMensagemCitadaNoModal || historicoAtendimento
            }
            historicoAtendimento={historicoAtendimento}
            actions={(() => {
              const restrictActions =
                actions === false ||
                messageData.isOfficialWhatsAppChannelWithClosedSession ||
                messageData.isIntagramChannelWithClosedSession ||
                atendimentoSelecionado?.contato?.bloqueadoEm ||
                atendimentoSelecionado?.contato?.blockedAt ||
                isEncaminhandoMensagens;

              if (restrictActions) {
                const downloadAction = defaultMessageActions.find(
                  a => (a as React.ReactElement)?.key === 'download',
                );
                return downloadAction ? [downloadAction] : false;
              }

              return defaultMessageActions;
            })()}
            onReenviarMensagem={() => {
              if (onReenviarMensagem) onReenviarMensagem(mensagem);
            }}
            onVerHistoricoMensagemEditada={() => {
              if (onVerHistoricoMensagemEditada) {
                onVerHistoricoMensagemEditada(messageData.id);
              }
            }}
          />
        ) : (
          <FallbackMessage
            fromMe={messageData.fromMe}
            autor={
              !messageData.fromMe && !isGroup ? '' : messageData.customAutor
            }
            autorColor={messageData.autorColor}
            apagada={Boolean(messageData.mensagemApagadaEm)}
            encaminhada={messageData.isEncaminhada}
            mensagem={mensagem}
            isGroup={isGroup}
          />
        )}
      </Box>
    </div>
  );
}

export default React.memo(MessageItem, (prevProps, nextProps) => {
  return (
    prevProps.mensagem === nextProps.mensagem &&
    prevProps.isGroup === nextProps.isGroup &&
    prevProps.verMensagemCitadaNoModal === nextProps.verMensagemCitadaNoModal &&
    prevProps.historicoAtendimento === nextProps.historicoAtendimento
  );
});
